import { nextTick, shallowRef } from 'vue';
import type { School } from '@/types/composition-functions';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { withStaffRedirect } from '@/utils/authRedirect';

export type SchoolDestination = Partial<Pick<School, 'id' | 'name' | 'school_type' | 'student_portal_mode'>>;
export const SCHOOL_SWITCH_KEY = 'pending_school_switch';
export const departingSchool = shallowRef<SchoolDestination | null>(null);
export const schoolDepartureError = shallowRef('');

// These legacy keys are not tenant-scoped. User preferences and credentials stay.
const schoolStorageKeys = [
  'user', 'settings', 'totalSchoolDay', 'studentAttendance', 'studentSession',
  'school_type', 'student_portal_mode', 'session_id', 'schoolId', 'subjectsData',
  'selectedTerm', 'selectedClassroom', 'teacherRemark', 'principalRemark',
];

export function readSchoolDestination(): SchoolDestination | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(SCHOOL_SWITCH_KEY) || 'null');
    return value && typeof value.id === 'string' && value.id ? value : null;
  } catch {
    return null;
  }
}

export function clearSchoolSelections() {
  for (const key of schoolStorageKeys) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
}

/**
 * Unmount the old tenant before changing the shared ID. Do not call selectSchool:
 * it starts queries in the old application's Apollo cache before navigation.
 */
export async function reloadForSchool(
  school: SchoolDestination,
  followSharedSchool = false,
  returnTo?: string | null,
): Promise<void> {
  if (departingSchool.value) return;
  if (!school.id) throw new Error('Choose a school before switching.');
  let destination = school;
  schoolDepartureError.value = '';
  departingSchool.value = school;
  const previousId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
  try {
    await nextTick();
    // Let the opaque transition paint before leaving this document.
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (followSharedSchool) {
      const sharedId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
      if (!sharedId) throw new Error('No school is selected.');
      destination = { id: sharedId, name: 'Your selected school' };
    }
    sessionStorage.setItem(SCHOOL_SWITCH_KEY, JSON.stringify(destination));
    clearSchoolSelections();
    if (destination.school_type) sessionStorage.setItem('school_type', destination.school_type);
    if (destination.student_portal_mode) sessionStorage.setItem('student_portal_mode', destination.student_portal_mode);
    if (!followSharedSchool) localStorage.setItem(LocalStorageKeys.currentSchoolId, destination.id!);
    window.location.replace(withStaffRedirect('/switch-school', returnTo));
  } catch (error) {
    if (followSharedSchool) {
      // The shared tenant already changed; never reveal this tab's old workspace.
      schoolDepartureError.value = 'Your school changed in another tab. Try again to open its workspace.';
      return;
    }
    if (!followSharedSchool && previousId) localStorage.setItem(LocalStorageKeys.currentSchoolId, previousId);
    else if (!followSharedSchool) localStorage.removeItem(LocalStorageKeys.currentSchoolId);
    sessionStorage.removeItem(SCHOOL_SWITCH_KEY);
    departingSchool.value = null;
    throw error;
  }
}

export function retrySchoolDeparture() {
  const school = departingSchool.value;
  departingSchool.value = null;
  if (school) void reloadForSchool(school, true);
}

export function chooseSchoolAfterDeparture() {
  window.location.replace('/select-school');
}
